import { TableContext } from "../context";
import React, { useState, useContext, useEffect, useMemo } from "react";
import "../App.css";
import Papa from "papaparse";
import { IoMdDownload } from "react-icons/io";
import { saveAs } from "file-saver";
import { DateTime } from 'luxon';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import {
    McTable,
    McButton,
    McPagination
} from "@maersk-global/mds-react-wrapper";
import FilterButton from "./FilterButton";

/**
 * Table component for displaying and filtering table data.
 * Provides options to filter data by risk, state, type, and category.
 * Allows downloading the filtered data as a CSV file.
 * 
 * @param {Object} props - The component props.
 * @param {Array} props.data - The table data to display.
 * @param {string} props.Header1 - The first header for the table.
 * @param {string} props.Header2 - The second header for the table.
 * @param {string} props.Header3 - The third header for the table.
 * @param {string} props.Header4 - The fourth header for the table.
 * @param {string} props.Header5 - The fifth header for the table.
 * @param {string} props.Header6 - The sixth header for the table.
 * @param {string} props.Header7 - The seventh header for the table.
 * @param {string} props.Header8 - The eighth header for the table.
 * @param {string} props.Header9 - The ninth header for the table.
 * @param {string} props.Header10 - The tenth header for the table.
 * @param {string} props.Header11 - The eleventh header for the table.
 */
export default function LandingPageTable(props) {
    const { tableData: contextTableData } = useContext(TableContext);
    
    // Use the data prop if provided, otherwise fall back to context
    const tableData = props.data || contextTableData || [];
    // const [selectedRisk, setSelectedRisk] = useState("");
    // const [selectedState, setSelectedState] = useState("");
    // const [selectedType, setSelectedType] = useState("");
    // const [selectedRelation, setSelectedRelation] = useState("");
    // const [selectedCategorization, setSelectedCategorization] = useState("");
    // const [selectedTargetApplication, setSelectedTargetApplication] = useState("");
    const [activeFilters, setActiveFilters] = useState([]);
    const [tableStyles, setTableStyles] = useState();

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const recordsPerPage = 100; // Number of records per page

    // Table state preservation
    const [sortColumn, setSortColumn] = useState("plannedStartDate");
    const [sortDirection, setSortDirection] = useState("descending");
    const [tableScrollPosition, setTableScrollPosition] = useState(0);

    // Define table columns for the filter system and McTable
    const tableColumns = [
        {
            id: "changeNumber",
            label: props.Header1 || "Change Number",
            sortable: true,
        },
        {
            id: "configurationItem",
            label: props.Header2 || "Configuration Item",
            sortable: true,
        },
        {
            id: "risk",
            label: props.Header3 || "Risk",
            sortable: true,
        },
        {
            id: "shortDescription",
            label: props.Header4 || "Short Description",
            sortable: true,
            nowrap: false,
        },
        {
            id: "plannedStartDate",
            label: props.Header5 || "Planned Start Date",
            sortable: true,
        },
        {
            id: "plannedEndDate",
            label: props.Header6 || "Planned End Date",
            sortable: true,
        },
        {
            id: "state",
            label: props.Header7 || "State",
            sortable: true,
        },
        {
            id: "type",
            label: props.Header8 || "Type",
            sortable: true,
        },
        {
            id: "assignmentGroup",
            label: props.Header9 || "Assignment Group",
            sortable: true,
        },
        {
            id: "requestedBy",
            label: props.Header10 || "Requested By",
            sortable: true,
            // width: "100px",
        },
        {
            id: "closeCode",
            label: props.Header11 || "Close Code",
            sortable: true,
            // width: "100px",
        }
    ];

    // Filter columns for the advanced filter (using original field names)
    const filterColumns = [
        { id: "Change Number", label: "Change Number" },
        // { id: "Configuration Item", label: "Configuration Item" },
        { id: "Risk", label: "Risk" },
        { id: "State", label: "State" },
        { id: "Type", label: "Type" },
        { id: "Category", label: "Category" },
        { id: "Relation", label: "Relation" },
        { id: "Short Description", label: "Short Description" },
        { id: "Planned Start Date", label: "Planned Start Date" },
        { id: "Planned End Date", label: "Planned End Date" },  
        { id: "Assignment Group", label: "Assignment Group" },
        { id: "Requested By", label: "Requested By" },
        { id: "Close Code", label: "Close Code" }
    ];

    // const handleTargetApplicationChange = (e) => setSelectedTargetApplication(e.target.value);
    // const handleRiskChange = (e) => setSelectedRisk(e.target.value);
    // const handleStateChange = (e) => setSelectedState(e.target.value);
    // const handleTypeChange = (e) => setSelectedType(e.target.value);
    // const handleCategorizationChange = (e) => setSelectedCategorization(e.target.value);
    // const handleRelationChange = (e) => setSelectedRelation(e.target.value);

    const calculateDifferenceInHours = (startDate, endDate) => {
        const start = DateTime.fromFormat(startDate, 'yyyy-MM-dd HH:mm:ss');
        const end = DateTime.fromFormat(endDate, 'yyyy-MM-dd HH:mm:ss');
        return end.diff(start, 'hours').hours;
    };

    const sortArrayByProperty = (data, property, order = 'ascending') => {
        return data.sort((a, b) => {
            let comparison = 0;

            if (property === "plannedStartDate" || property === "plannedEndDate") {
                const dateA = DateTime.fromFormat(a[property], 'dd-MM-yyyy HH:mm:ss');
                const dateB = DateTime.fromFormat(b[property], 'dd-MM-yyyy HH:mm:ss');
                if (dateA < dateB) comparison = -1;
                else if (dateA > dateB) comparison = 1;
            }
            else if (property === "changeNumber") {
                const valA = a[property];
                const valB = b[property];
                const numA = typeof valA === "number" ? valA : parseInt(valA.replace(/\D/g, ""), 10);
                const numB = typeof valB === "number" ? valB : parseInt(valB.replace(/\D/g, ""), 10);

                if (!isNaN(numA) && !isNaN(numB)) {
                    if (numA < numB) comparison = -1;
                    else if (numA > numB) comparison = 1;
                } else {
                    if (String(valA) < String(valB)) comparison = -1;
                    else if (String(valA) > String(valB)) comparison = 1;
                }
            }
            else {
                if (a[property] > b[property]) {
                    comparison = 1;
                } else if (a[property] < b[property]) {
                    comparison = -1;
                }
            }
            return order === 'ascending' ? comparison : comparison * -1;
        });
    }

    // Apply advanced filters from the filter menu
    const applyAdvancedFilters = (data, filters) => {
        if (!filters || filters.length === 0) return data;

        return data.filter((item) => {
            return filters.every((filter) => {
                let itemValue = item[filter.field];
                const filterValue = filter.value.toLowerCase();

                if (itemValue === undefined || itemValue === null) return false;

                if (
                    filter.field === "Planned Start Date" ||
                    filter.field === "Planned End Date"
                ) {
                    const dt = DateTime.fromFormat(itemValue, "yyyy-MM-dd HH:mm:ss");
                    itemValue = dt.isValid ? dt.toFormat("dd-MM-yyyy HH:mm:ss") : "";
                }

                const itemString = String(itemValue).toLowerCase();

                switch (filter.operator) {
                    case "contains":
                        return itemString.includes(filterValue);
                    case "Does not contain":
                        return !itemString.includes(filterValue);
                    case "equals":
                        return itemString === filterValue;
                    case "Is not":
                        return itemString !== filterValue;
                    case "startsWith":
                        return itemString.startsWith(filterValue);
                    case "endsWith":
                        return itemString.endsWith(filterValue);
                    default:
                        return true;
                }
            });
        });
    };

    const filteredData = useMemo(() => {
        console.log('LandingPageTable - tableData received:', tableData);
        console.log('LandingPageTable - tableData length:', tableData?.length || 0);
        console.log('LandingPageTable - isLoading prop:', props.isLoading);
        
        if (!tableData || tableData.length === 0) {
            console.log('LandingPageTable - No table data available');
            return [];
        }

        // Apply advanced filters
        let filtered = applyAdvancedFilters(tableData, activeFilters);

        filtered.sort((a, b) => {
            const dateA = DateTime.fromFormat(a["Planned Start Date"], 'yyyy-MM-dd HH:mm:ss');
            const dateB = DateTime.fromFormat(b["Planned Start Date"], 'yyyy-MM-dd HH:mm:ss');
            if (dateA < dateB) return 1;
            if (dateA > dateB) return -1;
            return 0;
        });

        return filtered;
    }, [tableData, activeFilters]);

    // Client-side pagination - slice data before transformation
    const totalPages = Math.ceil(filteredData.length / recordsPerPage);
    const paginatedData = filteredData.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage);

    const transformedData = useMemo(() => {
        let dataToSort = paginatedData.map((item, index) => {
            const differenceInHours = calculateDifferenceInHours(item["Planned Start Date"], item["Planned End Date"]);

            return {
                id: `row-${index}`,
                changeNumber: item["Change Number"],
                configurationItem: item["Configuration Item"],
                risk: item["Risk"],
                shortDescription: item["Short Description"],
                plannedStartDate: DateTime.fromFormat(item["Planned Start Date"], 'yyyy-MM-dd HH:mm:ss').toFormat('dd-MM-yyyy HH:mm:ss'),
                plannedEndDate: DateTime.fromFormat(item["Planned End Date"], 'yyyy-MM-dd HH:mm:ss').toFormat('dd-MM-yyyy HH:mm:ss'),
                state: item["State"],
                type: item["Type"],
                assignmentGroup: item["Assignment Group"],
                requestedBy: item["Requested By"],
                closeCode: item["Close Code"],
                _originalData: item,
                _rowStyle: differenceInHours >= 48 ? { backgroundColor: '#EDEDED' } : {}
            };
        });

        if (sortColumn) {
            dataToSort = sortArrayByProperty([...dataToSort], sortColumn, sortDirection);
        }

        return dataToSort;
    }, [paginatedData, sortColumn, sortDirection]);

    const handlePageChange = (event) => {
        setCurrentPage(event.detail);
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [filteredData.length]);

    useEffect(() => {
        if (tableScrollPosition > 0) {
            const tableElement = document.querySelector('mc-table');
            if (tableElement) {
                const scrollContainer = tableElement.shadowRoot?.querySelector('.table-container') ||
                    tableElement.querySelector('.table-container');
                if (scrollContainer) {
                    scrollContainer.scrollTop = tableScrollPosition;
                }
            }
        }
    }, [transformedData]);

    useEffect(() => {
        const styledRows = transformedData.filter(row =>
            row._rowStyle && row._rowStyle.backgroundColor === '#EDEDED'
        );

        setTableStyles(`
            ${styledRows
                .map((row) => {
                    return `
                        .mds-table .row-${row.id} td {
                        border-left: 4px solid #EDEDED;
                        background-color: #EDEDED !important;
                        }
                    `;
                })
                .join("\n")}
            .mds-table {
                max-height: 65vh !important;
            }
        `);
    }, [transformedData]);

    // const getUniqueValues = (key) => {
    //     if (!tableData) return [];

    //     if (key === "Risk" && selectedRisk) {
    //         return [...new Set(tableData.map(item => item[key]))];
    //     }
    //     if (key === "State" && selectedState) {
    //         return [...new Set(tableData.map(item => item[key]))];
    //     }
    //     if (key === "Type" && selectedType) {
    //         return [...new Set(tableData.map(item => item[key]))];
    //     }
    //     if (key === "Category" && selectedCategorization) {
    //         return [...new Set(tableData.map(item => item[key]))];
    //     }
    //     if (key === "Relation") {
    //         const uniqueValues = new Set();
    //         filteredData.forEach(item => {
    //             if (item[key]) {
    //                 const values = item[key].split(',');
    //                 values.forEach(value => {
    //                     if (value.includes("Target")) {
    //                         uniqueValues.add("Target");
    //                     }
    //                     if (value.includes("Upstream")) {
    //                         uniqueValues.add("Upstream");
    //                     }
    //                     if (value.includes("Downstream")) {
    //                         uniqueValues.add("Downstream");
    //                     }
    //                 });
    //             }
    //         });
    //         return [...uniqueValues].sort();
    //     }
    //     if (key === "Target Application") {
    //         const uniqueApplications = new Set();
    //         filteredData.forEach(item => {
    //             if (item["Relation"]) {
    //                 const toMatches = item["Relation"].match(/to\s([^,]+)/g);
    //                 if (toMatches) {
    //                     toMatches.forEach(match => {
    //                         const application = match.split('to ')[1].trim();
    //                         uniqueApplications.add(application);
    //                     });
    //                 }

    //                 const targetAppMatches = item["Relation"].match(/Target Application\s([^,]+)/g);
    //                 if (targetAppMatches) {
    //                     targetAppMatches.forEach(match => {
    //                         const application = match.split('Target Application ')[1].trim();
    //                         uniqueApplications.add(application);
    //                     });
    //                 }
    //             }
    //         });
    //         return [...uniqueApplications].sort();
    //     }
    //     return [...new Set(filteredData.map(item => item[key]))];
    // };

    const handleTableSorted = ({ detail: { column, direction } }) => {
        setSortColumn(column);
        setSortDirection(direction);

        // Preserve scroll position after sorting
        setTimeout(() => {
            const tableElement = document.querySelector('mc-table');
            if (tableElement && tableScrollPosition > 0) {
                const scrollContainer = tableElement.shadowRoot?.querySelector('.table-container') ||
                    tableElement.querySelector('.table-container');
                if (scrollContainer) {
                    scrollContainer.scrollTop = tableScrollPosition;
                }
            }
        }, 100);
    }

    // Save scroll position before interactions
    const saveScrollPosition = () => {
        const tableElement = document.querySelector('mc-table');
        if (tableElement) {
            const scrollContainer = tableElement.shadowRoot?.querySelector('.table-container') ||
                tableElement.querySelector('.table-container');
            if (scrollContainer) {
                setTableScrollPosition(scrollContainer.scrollTop);
            }
        }
    };

    // Optimized click handlers that preserve table state
    const handleChangeNumberClick = (rowData, drawerType) => {
        saveScrollPosition();
        const changeNumber = rowData["Change Number"];
        if (changeNumber) {
            // Open change details in a new tab
            const url = `/change_details?changeNumber=${encodeURIComponent(changeNumber)}`;
            window.open(url, '_blank');
        }
    };

    const handleCiInfoClick = (rowData) => {
        saveScrollPosition();

        setTimeout(() => {
            props.setisdrawerOpen(true);
            props.setDrawerCi(rowData["Configuration Item"]);
            props.setDrawerCiCategory(rowData["Category"]);
            props.setDrawerType("ci_info");
        }, 0);
    };


    const getCloseCodeColor = (closeCode) => {
        switch (closeCode.toLowerCase()) {
            case 'successful':
                return '#00786b';
            case 'success':
                return '#00786b';
            case 'unsuccessful':
                return '#e75858';
            case 'successful_issues':
                return '#d8d52c';
            default:
                return 'black';
        }
    };

    const handleDownloadClick = () => {
        const headers = [
            props.Header1,
            props.Header2,
            props.Header3,
            props.Header4,
            props.Header5,
            props.Header6,
            props.Header7,
            props.Header8,
            props.Header9,
            props.Header10,
            props.Header11,
        ];
        const csv = Papa.unparse({
            fields: headers,
            data: filteredData,
        });
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        saveAs(blob, "Change Management.csv");
    };

    const handleFilterApply = (filters) => {
        setActiveFilters(filters);
    };

    return (
        <div className="home-table-container" style={{ marginTop: 0, marginLeft: '40px', marginRight: '40px' }}>
            {/* <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <h2 style={{ fontWeight: "600", fontSize: '1.5rem', marginBottom: '7px' }}>
                    {headingText}
                    {filteredData.length > 0 && (
                        <span style={{ fontSize: '1rem', color: '#666', marginLeft: '10px' }}>
                            ({filteredData.length} records)
                        </span>
                    )}
                </h2>
            </div> */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '10px',
                    position: 'relative',
                }}
            >
                <div style={{ flex: '0 0 auto' }}>
                    <FilterButton
                        columns={filterColumns}
                        data={tableData || []}
                        onApply={handleFilterApply}
                        activeFilters={activeFilters}
                        setActiveFilters={setActiveFilters}
                    />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flex: '0 0 auto', marginLeft: 'auto' }}>
                    {!props.isLoading && 
                        <span style={{ fontSize: '1rem', color: '#0077b2' }}>
                            Total: {filteredData.length}
                        </span>
                    }
                    <McButton
                        label="Download"
                        appearance="primary"
                        variant="filled"
                        fit="small"
                        click={handleDownloadClick}
                    >
                        <span slot="trailingicon">
                            <mc-icon class="my-icon" icon="tray-arrow-down" color="white"></mc-icon>
                        </span>
                    </McButton>
                </div>
            </div>
            <div>
                <McTable
                    data={transformedData}
                    columns={tableColumns}
                    datakey="id"
                    headersticky
                    fit="small"
                    sortmanual
                    sortchange={handleTableSorted}
                    sortdefaultcolumnid={sortColumn}
                    sortdefaultdirection={sortDirection}
                    customstyles={tableStyles}
                    resetscrollonpagechange={false}
                    stateslotheight="200px"
                >
                    {props.isLoading ? (
                        <mc-loading-indicator slot="state"></mc-loading-indicator>
                    ) : (
                        transformedData?.length === 0 && (
                            <div slot="state" style={{ textAlign: "center", padding: "2rem", fontSize: "1.5rem", fontWeight: "600" }}>
                                No Data Found
                            </div>
                        )
                    )}
                    {transformedData?.map((row) => (
                        <React.Fragment key={row.id}>
                            {/* Change Number Cell */}
                            <div
                                slot={`${row.id}_changeNumber`}
                            >
                                {/^CHG/.test(row._originalData["Change Number"]) ? (
                                    <span
                                        onClick={() => handleChangeNumberClick(row._originalData, "change_details")}
                                        style={{
                                            color: "#0077b2",
                                            textDecoration: "none",
                                            cursor: "pointer"
                                        }}
                                    >
                                        {row.changeNumber}
                                    </span>
                                ) : (
                                    <span
                                        onClick={() => handleChangeNumberClick(row._originalData, "deployment_details")}
                                        style={{
                                            color: "#0077b2",
                                            textDecoration: "none",
                                            cursor: "pointer"
                                        }}
                                    >
                                        {row.changeNumber}
                                    </span>
                                )}
                            </div>

                            {/* Configuration Item Cell */}
                            <div
                                slot={`${row.id}_configurationItem`}
                                style={{
                                    cursor: row._originalData["Category"] === "Application" ? "pointer" : undefined
                                }}
                                onClick={row._originalData["Category"] === "Application" ? () => handleCiInfoClick(row._originalData) : undefined}
                            >
                                <div>
                                    {row._originalData["Category"] === "Application" ? (
                                        <div style={{ alignItems: 'center' }}>
                                            {row.configurationItem}
                                            <Tooltip title="Show CI Info">
                                                <IconButton
                                                    style={{
                                                        marginLeft: '0.2rem',
                                                        padding: 0,
                                                        verticalAlign: 'top'
                                                    }}
                                                    tabIndex={-1}
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        handleCiInfoClick(row._originalData);
                                                    }}
                                                    aria-label="Show CI info"
                                                >
                                                    <InfoOutlinedIcon fontSize="small" style={{ fontWeight: "normal" }} />
                                                </IconButton>
                                            </Tooltip>
                                        </div>
                                    ) : (
                                        row.configurationItem
                                    )}
                                    {row._originalData["Relation"] && (
                                        <div style={{ fontSize: '0.75rem', color: '#666666' }}>
                                            {row._originalData["Relation"]}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Close Code Cell */}
                            <div
                                slot={`${row.id}_closeCode`}
                            >
                                <span style={{ color: getCloseCodeColor(row.closeCode) }}>
                                    {row.closeCode}
                                </span>
                            </div>

                            <div
                                slot={`${row.id}_shortDescription`}
                            >
                                <span style={{ maxWidth: "400px", wordBreak: "break-word", fontSize: "16px" }}>
                                    {row.shortDescription}
                                </span>
                            </div>
                        </React.Fragment>
                    ))}
                </McTable>

                {/* Pagination Component */}
                {!props.isLoading && filteredData.length > recordsPerPage && (
                    <div style={{ paddingTop: '1rem', display: 'flex', justifyContent: 'center' }}>
                        <McPagination
                            currentpage={currentPage}
                            totalpages={totalPages}
                            pagechange={handlePageChange}
                        />
                    </div>
                )}
            </div>
            <div style={{ marginTop: "8vh", visibility: "hidden" }}>
                ""
            </div>
        </div>
    );
}